import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/lib/i18n";
import LanguageToggle from "@/components/LanguageToggle";
import Logo from "@/components/Logo";
import { Check, ArrowLeft, Sparkles } from "lucide-react";
import { toast } from "sonner";

const FEATURES = ["priceFeat1", "priceFeat2", "priceFeat3", "priceFeat4", "priceFeat5"];

const fmtEur = (cents) => `€${(Number(cents || 0) / 100).toFixed(2).replace(".", ",")}`;

function DynamicPlanCard({ plan, highlighted, mostPopular, onPickMonthly, onPickYearly, busy, t, user }) {
  const hasMonthly = plan.price_monthly_cents > 0;
  const hasYearly = plan.price_yearly_cents > 0;
  const isFree = !hasMonthly && !hasYearly;
  const daysMonthly = Number(plan.days_monthly || 0);
  const daysYearly = Number(plan.days_yearly || 0);
  const isPack = daysMonthly > 0 && !hasYearly;
  const yearlyDiscount = hasMonthly && hasYearly && plan.price_yearly_cents < plan.price_monthly_cents * 12
    ? Math.round(100 - (plan.price_yearly_cents / (plan.price_monthly_cents * 12)) * 100)
    : 0;

  const daysLabel = (n) => {
    if (n >= 365) return t("priceDurationYear");
    if (n === 30) return t("priceDurationMonth1");
    if (n >= 30) return t("priceDurationMonths", { n: Math.round(n / 30) });
    return t("priceDurationDays", { n });
  };

  // Compact horizontal feature bullets — 2 columns on md+ for legibility
  const features = [];
  features.push(plan.max_cvs < 0 ? t("pricePlanUnlimitedCvs") : t("pricePlanMaxCvs").replace("{n}", plan.max_cvs));
  features.push(plan.max_letters_per_month < 0 ? t("pricePlanUnlimitedLetters") : t("pricePlanMaxLetters").replace("{n}", plan.max_letters_per_month));
  features.push(plan.max_applications < 0 ? t("pricePlanUnlimitedApps") : t("pricePlanMaxApps").replace("{n}", plan.max_applications));
  features.push(plan.max_downloads_per_month < 0 ? t("pricePlanUnlimitedDownloads") : t("pricePlanMaxDownloads").replace("{n}", plan.max_downloads_per_month));
  features.push(t("pricePlanLangs").replace("{n}", plan.max_languages));
  if (plan.allow_public_cv) features.push(t("pricePlanPublicCv"));
  if (plan.allow_ai_interview) features.push(t("pricePlanAiInterview"));

  return (
    <div
      data-testid={`plan-card-${plan.slug}`}
      className={`relative border transition-colors ${
        highlighted ? "bg-[#24211D] text-[#F9F8F6] border-[#24211D]" : "bg-white border-black/10"
      }`}
    >
      {mostPopular && (
        <div
          data-testid={`plan-most-popular-${plan.slug}`}
          className="absolute -top-3 left-6 inline-flex items-center gap-1.5 bg-emerald-600 text-white px-3 py-1 font-mono text-[0.6rem] uppercase tracking-[0.15em] z-10"
        >
          <Sparkles size={11} strokeWidth={2} /> {t("priceMostPopular")}
        </div>
      )}
      {highlighted && (
        <div className="absolute -top-3 right-6 bg-amber-400 text-neutral-900 px-3 py-1 font-mono text-[0.6rem] uppercase tracking-[0.15em] shadow-md z-10">
          {t("priceBestValue")}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 items-center p-6 md:p-8">
        {/* Left: name + description */}
        <div className="md:col-span-4 min-w-0">
          <p className="font-serif text-2xl leading-tight">{plan.name}</p>
          {plan.description && (
            <p className={`text-sm mt-2 leading-relaxed ${highlighted ? "text-neutral-300" : "text-neutral-600"}`}>
              {plan.description}
            </p>
          )}
        </div>

        {/* Middle-left: price */}
        <div className="md:col-span-3 min-w-0">
          {isFree ? (
            <span className="font-serif text-4xl md:text-5xl">{t("priceFree")}</span>
          ) : hasMonthly ? (
            <>
              <div className="flex items-baseline gap-2 whitespace-nowrap flex-wrap">
                <span className="font-serif text-4xl md:text-5xl">{fmtEur(plan.price_monthly_cents)}</span>
                <span className={`font-mono text-[0.65rem] uppercase tracking-[0.15em] ${highlighted ? "text-neutral-300" : "text-neutral-500"}`}>
                  {isPack ? t("priceOneTime") : t("pricePerMonth")}
                </span>
              </div>
              {isPack && daysMonthly >= 30 && (
                <p className={`font-mono text-[0.6rem] mt-1 ${highlighted ? "text-neutral-300" : "text-neutral-600"}`}>
                  {t("pricePackOnlyPerMonth")
                    .replace("{amount}", (plan.price_monthly_cents / (daysMonthly / 30) / 100).toFixed(2).replace(".", ","))
                    .replace("{duration}", daysLabel(daysMonthly).toLowerCase())}
                </p>
              )}
              {hasYearly && (
                <p className={`font-mono text-[0.65rem] mt-1 ${highlighted ? "text-neutral-300" : "text-neutral-500"}`}>
                  {fmtEur(plan.price_yearly_cents)} / {daysYearly > 0 ? daysLabel(daysYearly) : t("pricePerYear")}
                </p>
              )}
              {yearlyDiscount > 0 && (
                <span
                  data-testid={`plan-save-badge-${plan.slug}`}
                  className="inline-block mt-2 rounded-full bg-amber-300 text-[#1A1A1A] px-2.5 py-0.5 font-mono text-[0.6rem] tracking-[0.08em]"
                >
                  {t("priceSaveBadge").replace(/\d+%/, `${yearlyDiscount}%`)}
                </span>
              )}
            </>
          ) : null}
        </div>

        {/* Middle-right: feature bullets in 2-col grid */}
        <div className="md:col-span-3">
          <ul className={`grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 font-mono text-[0.7rem] ${highlighted ? "text-neutral-200" : "text-neutral-700"}`}>
            {features.map((f, i) => (
              <li key={i} className="flex items-start gap-2 leading-tight">
                <Check size={12} strokeWidth={2} className="shrink-0 mt-0.5" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Right: CTA */}
        <div className="md:col-span-2 flex flex-col gap-2 md:items-stretch">
          {isFree && (
            <button
              data-testid={`plan-cta-${plan.slug}-free`}
              onClick={onPickMonthly}
              disabled={busy}
              className={`inline-flex items-center justify-center gap-2 px-4 py-3 font-mono text-[0.65rem] uppercase tracking-[0.14em] transition-colors disabled:opacity-60 ${
                highlighted ? "bg-[#F9F8F6] text-[#1A1A1A] hover:bg-white" : "bg-[#24211D] text-[#F9F8F6] hover:bg-[#3D3934]"
              }`}
            >
              {busy ? "…" : (user ? t("priceCtaFree") : t("priceCtaLogin"))}
            </button>
          )}
          {hasMonthly && (
            <button
              data-testid={`plan-cta-${plan.slug}-monthly`}
              onClick={onPickMonthly}
              disabled={busy}
              className={`inline-flex items-center justify-center gap-2 px-4 py-3 font-mono text-[0.65rem] uppercase tracking-[0.14em] transition-colors disabled:opacity-60 text-center ${
                highlighted ? "bg-[#F9F8F6] text-[#1A1A1A] hover:bg-white" : "bg-[#24211D] text-[#F9F8F6] hover:bg-[#3D3934]"
              }`}
            >
              {busy ? "…" : (user ? (isPack ? t("priceCtaPack") : `${t("priceCta")} — ${t("pricePerMonth")}`) : t("priceCtaLogin"))}
            </button>
          )}
          {hasYearly && (
            <button
              data-testid={`plan-cta-${plan.slug}-yearly`}
              onClick={onPickYearly}
              disabled={busy}
              className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 font-mono text-[0.65rem] uppercase tracking-[0.14em] transition-colors disabled:opacity-60 border ${
                highlighted ? "border-[#F9F8F6]/40 text-[#F9F8F6] hover:bg-white/10" : "border-neutral-900 text-neutral-900 hover:bg-neutral-900 hover:text-[#F9F8F6]"
              }`}
            >
              {busy ? "…" : (user ? `${t("priceCta")} — ${t("pricePerYear")}` : t("priceCtaLogin"))}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Pricing() {
  const { t } = useT();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [plans, setPlans] = useState([]);
  const [popularSlug, setPopularSlug] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/plans"),
      api.get("/plans/popular").catch(() => ({ data: { slug: null } })),
    ]).then(([p, pop]) => {
      setPlans((p.data || []));
      setPopularSlug(pop.data?.slug || null);
    }).catch(() => setPlans([]));
  }, []);

  // Deep-link: /pricing?buy=<slug> auto-triggers checkout for the matching
  // pack. Used by the rebuy-reminder emails so recipients land straight in
  // Stripe. Only fires once (guarded by autoBuyTriggeredRef).
  const autoBuyTriggeredRef = useRef(false);
  useEffect(() => {
    const buy = params.get("buy");
    if (!buy || autoBuyTriggeredRef.current) return;
    if (!user || plans.length === 0) return;
    const target = plans.find((p) => p.slug === buy);
    if (!target) return;
    autoBuyTriggeredRef.current = true;
    // Kick off the same checkout flow as if the user clicked the pack card
    pick((target.days_monthly > 0) ? target.slug : `${target.slug}_monthly`);
  }, [user, plans, params]); // eslint-disable-line react-hooks/exhaustive-deps

  const expired = params.get("expired") === "1" || user?.subscription_status === "expired";

  const pick = async (plan) => {
    if (!user) {
      const redirectUrl = window.location.origin + "/pricing" + window.location.search;
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      return;
    }
    setBusy(true);
    try {
      const promo = params.get("promo") || undefined;
      // Attribution: pick up ref + utm_medium from the current URL so the
      // affiliate conversion + poster A/B winner analytics can grade which
      // poster variant is driving actual sales. Both are optional.
      const ref = (params.get("ref") || "").trim() || undefined;
      const utm_medium = (params.get("utm_medium") || "").trim() || undefined;
      const { data } = await api.post("/billing/checkout", {
        plan,
        origin_url: window.location.origin,
        ...(promo ? { promo } : {}),
        ...(ref ? { ref } : {}),
        ...(utm_medium ? { utm_medium } : {}),
      });
      if (data?.activated) {
        // Free plan — no Stripe redirect, just refresh session and go to dashboard
        toast.success(t("priceFreeActivated"));
        window.location.href = "/dashboard";
        return;
      }
      if (data?.promo_applied) {
        toast.success(t("pricePromoApplied"));
      }
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("priceCheckoutFailed"));
      setBusy(false);
    }
  };

  return (
    <div className="ed-page min-h-screen">
      <header className="ed-header px-8 py-5 flex items-center justify-between">
        <Logo size="md" showTagline={false} className="mr-3" data-testid="pricing-logo" />
        <div className="flex items-center gap-4">
          <Link
            to="/partner"
            data-testid="pricing-nav-partner"
            className="font-mono text-xs uppercase tracking-[0.15em] text-neutral-600 hover:text-neutral-900 transition-colors hidden sm:inline"
          >
            {t("landNavPartner")}
          </Link>
          <LanguageToggle testId="pricing-language-toggle" />
          {user && (
            <button data-testid="pricing-back" onClick={() => navigate("/dashboard")} className="ed-btn ed-btn-outline">
              <ArrowLeft size={14} strokeWidth={1.5} /> <span className="hidden sm:inline">{t("priceBack")}</span>
            </button>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-8 py-16 lg:py-24">
        {expired && (
          <div className="mb-10 border border-black/10 bg-white p-5 flex items-start gap-3" data-testid="pricing-expired-banner">
            <Sparkles size={16} strokeWidth={1.5} className="mt-0.5 shrink-0" />
            <p className="font-serif text-lg italic text-neutral-800">{t("priceExpiredBanner")}</p>
          </div>
        )}

        <p className="ed-eyebrow" data-testid="pricing-eyebrow">{t("priceEyebrow")}</p>
        <h1 className="ed-h1 mt-4 max-w-3xl">{t("priceTitle")}</h1>
        <p className="mt-6 max-w-2xl text-neutral-700 leading-relaxed">{t("priceSubtitle")}</p>

        {/* Prominent Trial CTA — the primary conversion path for new visitors */}
        <div
          data-testid="pricing-trial-card"
          className="mt-10 border-2 border-emerald-500 bg-gradient-to-br from-emerald-50/70 via-white to-white p-8 sm:p-12 shadow-[0_20px_60px_-30px_rgba(16,185,129,0.35)]"
        >
          <div className="flex flex-col lg:flex-row lg:items-center gap-8">
            <div className="flex-1 min-w-0">
              <span className="inline-flex items-center gap-2 font-mono text-[0.65rem] uppercase tracking-[0.2em] text-emerald-700 bg-emerald-100 border border-emerald-300 px-2 py-1">
                <Sparkles size={11} strokeWidth={1.8} /> {t("priceTrialBadge")}
              </span>
              <p className="font-serif text-3xl sm:text-4xl mt-4 leading-tight">{t("priceStartTrialTitle")}</p>
              <p className="text-base text-neutral-700 mt-3 max-w-xl leading-relaxed">
                {t("priceStartTrialSub")}
              </p>
              <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-neutral-700 max-w-xl">
                <li className="flex items-center gap-2"><Check size={13} strokeWidth={2} className="text-emerald-600" /> {t("priceTrialInc1")}</li>
                <li className="flex items-center gap-2"><Check size={13} strokeWidth={2} className="text-emerald-600" /> {t("priceTrialInc2")}</li>
                <li className="flex items-center gap-2"><Check size={13} strokeWidth={2} className="text-emerald-600" /> {t("priceTrialInc3")}</li>
                <li className="flex items-center gap-2"><Check size={13} strokeWidth={2} className="text-emerald-600" /> {t("priceTrialInc4")}</li>
              </ul>
            </div>
            <div className="flex flex-col items-start lg:items-end gap-3 shrink-0">
              <button
                data-testid="plan-cta-free-signup"
                onClick={() => {
                  // On the pricing page the packs are already visible below —
                  // just scroll the visitor to the plan grid to pick one.
                  const target = document.querySelector('[data-testid="pricing-plans-eyebrow"]');
                  if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className="inline-flex items-center gap-3 bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-4 font-mono text-sm uppercase tracking-[0.15em] transition-colors shadow-lg"
              >
                {t("priceStartTrialCta")}
                <span aria-hidden="true">→</span>
              </button>
              <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500">
                {t("priceTrialNoCard")}
              </p>
            </div>
          </div>
        </div>

        <p className="ed-eyebrow mt-14" data-testid="pricing-plans-eyebrow">{t("priceOrCommit")}</p>

        {plans.length === 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
            <div className="p-10 border border-black/10 bg-white animate-pulse" />
            <div className="p-10 border border-black/10 bg-white animate-pulse" />
          </div>
        ) : (
          <div className="grid grid-cols-1 mt-4 gap-4">
            {(() => {
              // Compute which plan is admin-marked as "Meest gekozen".
              // Priority: an explicit `is_featured` flag beats the legacy
              // "last plan in the sorted list" heuristic so admins can flip
              // the highlighted card without touching sort_order.
              const featuredIdx = plans.findIndex((p) => p.is_featured);
              const fallbackIdx = plans.length - 1;
              const highlightedIdx = featuredIdx >= 0 ? featuredIdx : fallbackIdx;
              return plans.map((p, idx) => (
                <DynamicPlanCard
                  key={p.id}
                  plan={p}
                  highlighted={p.is_default ? false : idx === highlightedIdx}
                  mostPopular={popularSlug === p.slug}
                  busy={busy}
                  t={t}
                  user={user}
                  onPickMonthly={() => pick(
                  // Pack model: send slug directly (billing_api looks up
                  // days_monthly on the plan doc). Legacy plans keep the
                  // "_monthly" suffix.
                  (p.days_monthly > 0) ? p.slug
                    : (p.slug === "monthly" ? "monthly" : `${p.slug}_monthly`)
                )}
                onPickYearly={() => pick(
                  (p.days_yearly > 0) ? p.slug
                    : (p.slug === "yearly" ? "yearly" : `${p.slug}_yearly`)
                )}
              />
              ));
            })()}
          </div>
        )}

        <p className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-neutral-500 mt-6 text-center" data-testid="pricing-vat-note">
          {t("priceVatIncluded")}
        </p>

        {/* Post-trial Free tier — what happens after 7 days, so visitors
            know upfront that JobGrid never deletes their data. Highlighted
            with a soft rose accent because data-safety is a critical trust
            signal for hesitant buyers. */}
        <div className="mt-14 border-2 border-rose-300 bg-rose-50/70 p-8 shadow-[0_8px_32px_-16px_rgba(244,63,94,0.25)]" data-testid="pricing-free-tier">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_1.4fr] gap-8">
            <div>
              <p className="ed-eyebrow inline-flex items-center gap-2 text-rose-700">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" aria-hidden />
                {t("priceFreeEyebrow")}
              </p>
              <p className="font-serif text-2xl sm:text-3xl mt-2 leading-tight text-rose-950">{t("priceFreeTitle")}</p>
              <p className="text-sm text-neutral-700 mt-3 leading-relaxed">
                {t("priceFreeSub")}
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
              <div>
                <p className="ed-eyebrow mb-2 text-emerald-700">{t("priceFreeYesTitle")}</p>
                <ul className="space-y-1.5 text-sm text-neutral-700">
                  <li className="flex items-start gap-2"><Check size={13} strokeWidth={2} className="text-emerald-600 mt-0.5 shrink-0" /> {t("priceFreeYes1")}</li>
                  <li className="flex items-start gap-2"><Check size={13} strokeWidth={2} className="text-emerald-600 mt-0.5 shrink-0" /> {t("priceFreeYes2")}</li>
                  <li className="flex items-start gap-2"><Check size={13} strokeWidth={2} className="text-emerald-600 mt-0.5 shrink-0" /> {t("priceFreeYes3")}</li>
                </ul>
              </div>
              <div>
                <p className="ed-eyebrow mb-2 text-amber-700">{t("priceFreeNoTitle")}</p>
                <ul className="space-y-1.5 text-sm text-neutral-700">
                  <li className="flex items-start gap-2"><Sparkles size={13} strokeWidth={2} className="text-amber-600 mt-0.5 shrink-0" /> {t("priceFreeNo1")}</li>
                  <li className="flex items-start gap-2"><Sparkles size={13} strokeWidth={2} className="text-amber-600 mt-0.5 shrink-0" /> {t("priceFreeNo2")}</li>
                  <li className="flex items-start gap-2"><Sparkles size={13} strokeWidth={2} className="text-amber-600 mt-0.5 shrink-0" /> {t("priceFreeNo3")}</li>
                  <li className="flex items-start gap-2"><Sparkles size={13} strokeWidth={2} className="text-amber-600 mt-0.5 shrink-0" /> {t("priceFreeNo4")}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 border-t border-black/10 pt-10">
          <p className="ed-eyebrow">{t("priceFeatAll")}</p>
          <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {FEATURES.map((k) => (
              <li key={k} className="flex items-start gap-3">
                <Check size={15} strokeWidth={2} className="mt-0.5 shrink-0" />
                <span className="text-sm text-neutral-700 leading-relaxed">{t(k)}</span>
              </li>
            ))}
          </ul>
          <p className="font-mono text-xs text-neutral-500 mt-8" data-testid="pricing-cancel-note">{t("priceTrialNote")} {t("priceCancelNote")} {t("priceVatIncluded")}</p>
        </div>
      </main>
    </div>
  );
}
